import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { field_can_autoadvance, field_can_autosubmit } from "@tellescope/utilities";
import { AnswerForType } from "@tellescope/types-models";
import { useCallback, useEffect, useRef } from "react";
import { isChargebeeAnswerValue } from "../../utils";
import type { FormShallowStoreHook } from "../../store";
import { FieldChangeOptions, FormTypesWithValues } from "../../types";
import type { ImmediateSaveAnswer } from "./use-save-field-response";
import type { FieldStackEntry } from "./resolve-field-stack-entries";

export interface UseAutoAdvanceArgs {
  setFieldValue: <TField extends FormTypesWithValues>(fieldId: string, value: AnswerForType[TField]) => void;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  activeEntry: FieldStackEntry | undefined;
  goToNextFieldInternal: () => void;
  saveImmediateFieldResponse: (field: TellescopeModelFromName<"form_fields">, answer: ImmediateSaveAnswer) => void;
  useFormShallowStore: FormShallowStoreHook;
}

const AUTO_ADVANCE_DEBOUNCE_MS = 250;

export const useAutoAdvance = ({
  setFieldValue,
  fieldsMap,
  activeEntry,
  goToNextFieldInternal,
  saveImmediateFieldResponse,
  useFormShallowStore,
}: UseAutoAdvanceArgs) => {
  const autoAdvanceFieldChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const latestRef = useRef({ activeEntry, goToNextFieldInternal });
  latestRef.current = { activeEntry, goToNextFieldInternal };

  const submitFormRef = useRef<() => Promise<unknown>>(() => Promise.resolve());
  // Autosubmit must fire at most once per mount, so a failed attempt does not retry on the next
  // keystroke and a redirect-less success does not submit twice.
  const autoSubmitFiredRef = useRef(false);
  const {
    isAutoSubmitting,
    setIsAutoSubmitting,
    autoSubmitted,
    setAutoSubmitted,
    autoSubmitError,
    setAutoSubmitError,
    serverRedirectUrl,
  } = useFormShallowStore(
    "isAutoSubmitting",
    "setIsAutoSubmitting",
    "autoSubmitted",
    "setAutoSubmitted",
    "autoSubmitError",
    "setAutoSubmitError",
    "serverRedirectUrl"
  );

  // Cleanup of an external resource (a pending setTimeout) on unmount — the only
  // way to cancel a timer whose lifetime is tied to this hook's mount.
  useEffect(
    () => () => {
      if (autoAdvanceFieldChangeTimerRef.current) {
        clearTimeout(autoAdvanceFieldChangeTimerRef.current);
      }
    },
    []
  );

  const setFieldValueWithAutoAdvance = useCallback(
    <TField extends FormTypesWithValues>(
      fieldId: string,
      value: AnswerForType[TField],
      changeOptions?: FieldChangeOptions
    ) => {
      setFieldValue(fieldId, value);

      const field = fieldsMap[fieldId];
      if (!field) return;

      if (field.type === "Chargebee" && isChargebeeAnswerValue(value)) {
        saveImmediateFieldResponse(field, { type: "Chargebee", value });
      } else if (field.type === "Stripe" && typeof value === "string") {
        saveImmediateFieldResponse(field, { type: "Stripe", value });
      } else if (field.type === "Appointment Booking" && typeof value === "string") {
        saveImmediateFieldResponse(field, { type: "Appointment Booking", value });
      }

      if (changeOptions?.preventAutoAdvance) {
        if (autoAdvanceFieldChangeTimerRef.current) {
          clearTimeout(autoAdvanceFieldChangeTimerRef.current);
          autoAdvanceFieldChangeTimerRef.current = null;
        }
        return;
      }

      const canAutosubmit =
        !!field.options?.autoSubmit && field_can_autosubmit({ type: field.type, options: field.options });

      const canAutoAdvance =
        !!field.options?.autoAdvance && field_can_autoadvance({ type: field.type, options: field.options });

      if (!canAutosubmit && !canAutoAdvance) return;

      if (autoAdvanceFieldChangeTimerRef.current) {
        clearTimeout(autoAdvanceFieldChangeTimerRef.current);
      }
      autoAdvanceFieldChangeTimerRef.current = setTimeout(() => {
        autoAdvanceFieldChangeTimerRef.current = null;
        if (canAutosubmit) {
          if (autoSubmitFiredRef.current) return;
          autoSubmitFiredRef.current = true;
          setIsAutoSubmitting(true);
          submitFormRef.current().then(
            () => {
              setIsAutoSubmitting(false);
              setAutoSubmitted(true);
            },
            () => {
              setIsAutoSubmitting(false);
              setAutoSubmitError(true);
            }
          );
          return;
        }

        // Read after the debounce so the gate sees the answer that triggered it, not the render
        // this callback was created in.
        const latest = latestRef.current;
        if (latest.activeEntry?.error) return;

        latest.goToNextFieldInternal();
      }, AUTO_ADVANCE_DEBOUNCE_MS);
    },
    [setFieldValue, fieldsMap, saveImmediateFieldResponse, setIsAutoSubmitting, setAutoSubmitted, setAutoSubmitError]
  );

  return {
    setFieldValueWithAutoAdvance,
    submitFormRef,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    serverRedirectUrl,
  };
};
